import React from 'react'
import { AlertCircle, Upload, Loader2, X, Trash2, Calendar, ChevronDown, Video as VideoIcon, Play } from 'lucide-react'
import { useLanguage } from '@/contexts/LanguageContext'
import ErrorBanner from '@/components/ErrorBanner'
import type { useTurbineDetailViewModel } from '../useTurbineDetailViewModel'

type VM = ReturnType<typeof useTurbineDetailViewModel>

interface VideoSectionProps {
  isSuperAdmin: VM['isSuperAdmin']
  isCustomer: VM['isCustomer']
  weekOptions: VM['weekOptions']
  yearOptions: VM['yearOptions']
  selectedVideoType: VM['selectedVideoType']
  setSelectedVideoType: VM['setSelectedVideoType']
  selectedVideos: VM['selectedVideos']
  uploadingVideos: VM['uploadingVideos']
  videoUploadError: VM['videoUploadError']
  videoYear: VM['videoYear']
  setVideoYear: VM['setVideoYear']
  videoWeek: VM['videoWeek']
  setVideoWeek: VM['setVideoWeek']
  existingVideos: VM['existingVideos']
  loadingVideos: VM['loadingVideos']
  isVideoTypeDropdownOpen: VM['isVideoTypeDropdownOpen']
  setIsVideoTypeDropdownOpen: VM['setIsVideoTypeDropdownOpen']
  isVideoYearDropdownOpen: VM['isVideoYearDropdownOpen']
  setIsVideoYearDropdownOpen: VM['setIsVideoYearDropdownOpen']
  isVideoWeekDropdownOpen: VM['isVideoWeekDropdownOpen']
  setIsVideoWeekDropdownOpen: VM['setIsVideoWeekDropdownOpen']
  setPlayingVideoId: VM['setPlayingVideoId']
  videoTypeDropdownRef: VM['videoTypeDropdownRef']
  videoYearDropdownRef: VM['videoYearDropdownRef']
  videoWeekDropdownRef: VM['videoWeekDropdownRef']
  handleVideoSelect: VM['handleVideoSelect']
  removeSelectedVideo: VM['removeSelectedVideo']
  uploadVideos: VM['uploadVideos']
  deleteVideoHandler: VM['deleteVideoHandler']
  /** Video listesi yuklenemediginde dolu gelir. "Video yok" ile KARISTIRILMAZ. */
  videosLoadError: VM['videosLoadError']
  retryLoadVideos: VM['retryLoadVideos']
}

const VideoSection: React.FC<VideoSectionProps> = ({
  isSuperAdmin,
  isCustomer,
  weekOptions,
  yearOptions,
  selectedVideoType,
  setSelectedVideoType,
  selectedVideos,
  uploadingVideos,
  videoUploadError,
  videoYear,
  setVideoYear,
  videoWeek,
  setVideoWeek,
  existingVideos,
  loadingVideos,
  isVideoTypeDropdownOpen,
  setIsVideoTypeDropdownOpen,
  isVideoYearDropdownOpen,
  setIsVideoYearDropdownOpen,
  isVideoWeekDropdownOpen,
  setIsVideoWeekDropdownOpen,
  setPlayingVideoId,
  videoTypeDropdownRef,
  videoYearDropdownRef,
  videoWeekDropdownRef,
  handleVideoSelect,
  removeSelectedVideo,
  uploadVideos,
  deleteVideoHandler,
  videosLoadError,
  retryLoadVideos,
}) => {
  const { t } = useLanguage()

  return (
    <>
          {/* Video Upload Section - Hidden for Customer */}
          {!isCustomer && (
            <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
              <div className="flex items-center mb-4">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                  <VideoIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Türbin Videoları</h3>
                  <p className="text-sm text-blue-600">Video dosyalarını yükleyin (Maks: 500MB)</p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Filters and Type Selection Row */}
                <div className="flex flex-wrap items-center gap-2">
                  {/* Video Type Dropdown Chip */}
                  <div className="relative" ref={videoTypeDropdownRef}>
                    <button
                      onClick={() => setIsVideoTypeDropdownOpen(!isVideoTypeDropdownOpen)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-all"
                    >
                      <span>{selectedVideoType === 'blade-a' ? 'Blade A' : selectedVideoType === 'blade-b' ? 'Blade B' : selectedVideoType === 'blade-c' ? 'Blade C' : selectedVideoType === 'turbine' ? 'Turbine' : selectedVideoType === 'operator' ? 'Operator' : selectedVideoType === 'robot' ? 'Robot' : 'Drone'}</span>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isVideoTypeDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isVideoTypeDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] min-w-[140px]">
                        {['blade-a', 'blade-b', 'blade-c', 'turbine', 'operator', 'robot', 'drone'].map(type => (
                          <button
                            key={type}
                            onClick={() => {
                              setSelectedVideoType(type)
                              setIsVideoTypeDropdownOpen(false)
                            }}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-blue-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                              selectedVideoType === type ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {type === 'blade-a' ? 'Blade A' : type === 'blade-b' ? 'Blade B' : type === 'blade-c' ? 'Blade C' : type === 'turbine' ? 'Turbine' : type === 'operator' ? 'Operator' : type === 'robot' ? 'Robot' : 'Drone'}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Year Dropdown Chip */}
                  <div className="relative" ref={videoYearDropdownRef}>
                    <button
                      onClick={() => setIsVideoYearDropdownOpen(!isVideoYearDropdownOpen)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-blue-100 text-blue-700 hover:bg-blue-200 transition-all"
                    >
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{videoYear}</span>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isVideoYearDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isVideoYearDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto min-w-[120px]">
                        {yearOptions.map(year => (
                          <button
                            key={year}
                            onClick={() => {
                              setVideoYear(year)
                              setIsVideoYearDropdownOpen(false)
                            }}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-blue-50 first:rounded-t-lg last:rounded-b-lg transition-colors ${
                              videoYear === year ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {year}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Week Dropdown Chip */}
                  <div className="relative" ref={videoWeekDropdownRef}>
                    <button
                      onClick={() => setIsVideoWeekDropdownOpen(!isVideoWeekDropdownOpen)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        videoWeek !== 0
                          ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>{videoWeek === 0 ? 'Tüm Haftalar' : `${videoWeek}. Hafta`}</span>
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isVideoWeekDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isVideoWeekDropdownOpen && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl z-[9999] max-h-60 overflow-y-auto min-w-[140px]">
                        <button
                          onClick={() => {
                            setVideoWeek(0)
                            setIsVideoWeekDropdownOpen(false)
                          }}
                          className={`w-full px-4 py-2 text-left text-sm hover:bg-blue-50 first:rounded-t-lg transition-colors ${
                            videoWeek === 0 ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700'
                          }`}
                        >
                          Tüm Haftalar
                        </button>
                        {weekOptions.map(week => (
                          <button
                            key={week}
                            onClick={() => {
                              setVideoWeek(week)
                              setIsVideoWeekDropdownOpen(false)
                            }}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-blue-50 last:rounded-b-lg transition-colors ${
                              videoWeek === week ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-700'
                            }`}
                          >
                            {week}. Hafta
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Video Upload Area */}
                <div className="space-y-4">
                  <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-blue-300 border-dashed rounded-lg cursor-pointer bg-blue-50 hover:bg-blue-100 transition-colors">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <VideoIcon className="w-10 h-10 mb-2 text-blue-500" />
                      <p className="mb-2 text-sm text-blue-600">
                        <span className="font-semibold">Video Yükle</span> (MP4, MOV, AVI, WebM, MKV)
                      </p>
                      <p className="text-xs text-blue-500">Maksimum 500MB</p>
                    </div>
                    <input
                      type="file"
                      className="hidden"
                      accept="video/*"
                      multiple
                      onChange={handleVideoSelect}
                    />
                  </label>

                  {/* Error Message */}
                  <ErrorBanner message={videoUploadError} />

                  {/* Selected Videos Preview */}
                  {selectedVideos.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-gray-700">Seçili Videolar ({selectedVideos.length}):</p>
                      <div className="space-y-2">
                        {selectedVideos.map((video, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-white border border-blue-200 rounded-lg">
                            <div className="flex items-center gap-3">
                              <VideoIcon className="w-5 h-5 text-blue-600" />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{video.name}</p>
                                <p className="text-xs text-gray-500">{(video.size / 1024 / 1024).toFixed(1)} MB</p>
                              </div>
                            </div>
                            <button
                              onClick={() => removeSelectedVideo(index)}
                              className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={uploadVideos}
                        disabled={uploadingVideos}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {uploadingVideos ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span>Yükleniyor...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            <span>Videoları Yükle</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>

                {/* Existing Videos List - Compact & Scrollable */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">
                    Yüklenmiş Videolar ({existingVideos.length})
                  </p>

                  {/* Elde onceki liste varsa veri KORUNUR, ustune uyari bandi cikar (Kural 4). */}
                  {videosLoadError && existingVideos.length > 0 && (
                    <ErrorBanner message={videosLoadError} tone="warning" onRetry={retryLoadVideos} />
                  )}

                  {loadingVideos ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                      <span className="ml-2 text-gray-600">Videolar yükleniyor...</span>
                    </div>
                  ) : videosLoadError && existingVideos.length === 0 ? (
                    /* Kural 4: "yuklenemedi" ile "henuz video yok" gorsel olarak AYRISIR. */
                    <div className="text-center py-8">
                      <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                      <p className="text-sm font-medium text-gray-700">{t.errors.loadFailed}</p>
                      <p className="text-xs text-gray-500 mt-1">{videosLoadError}</p>
                      <button
                        type="button"
                        onClick={retryLoadVideos}
                        className="mt-3 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-medium transition-colors"
                      >
                        {t.errors.retry}
                      </button>
                    </div>
                  ) : existingVideos.length > 0 ? (
                    <div className="max-h-[400px] overflow-y-auto space-y-2 pr-2">
                      {existingVideos.map(video => (
                        <div key={video.id} className="flex items-center justify-between p-3 bg-white border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors group">
                          {/* Video Info */}
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {/* Play Button */}
                            <button
                              onClick={() => setPlayingVideoId(video.id)}
                              className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center hover:bg-blue-200 transition-colors"
                              title="Videoyu oynat"
                            >
                              <Play className="w-5 h-5 text-blue-600" />
                            </button>

                            {/* Video Details */}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 truncate">{video.original_filename}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-gray-500">
                                  {(video.file_size_bytes / 1024 / 1024).toFixed(1)} MB
                                </span>
                                <span className="text-xs text-gray-400">•</span>
                                <span className="text-xs text-gray-500">
                                  {new Date(video.uploaded_at).toLocaleDateString('tr-TR')}
                                </span>
                                {video.first_name && video.last_name && (
                                  <>
                                    <span className="text-xs text-gray-400">•</span>
                                    <span className="text-xs text-blue-600 font-medium truncate">
                                      {video.first_name} {video.last_name}
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Delete Button - Only SuperAdmin */}
                          {isSuperAdmin && (
                            <button
                              onClick={() => deleteVideoHandler(video.id)}
                              className="flex-shrink-0 p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100"
                              title="Videoyu sil"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <VideoIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-500">Henüz video yüklenmemiş</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
    </>
  )
}

export default VideoSection
